import { z } from 'zod';

/**
 * App-Cockpit (System): aggregierte Betriebs-Metriken + Gesundheitsstatus der
 * Infrastruktur (DB, Cache/Queue, Suche, Speicher, Backups), Laufzeit-Infos,
 * Datenbestand und Queue-Auslastung. Read-only, admin-only.
 */

export const cockpitHealthStatusSchema = z.enum(['up', 'degraded', 'down', 'not_configured']);
export type CockpitHealthStatus = z.infer<typeof cockpitHealthStatusSchema>;

/**
 * Ein überwachter Baustein (z. B. Datenbank, Redis, Backups).
 *
 * `detail`, `impact` und `remedy` beantworten drei verschiedene Fragen, und
 * genau deshalb sind es drei Felder (Anweisung Tech-Lead 2026-08-11: „wenn
 * etwas nicht passt, will ich Infos, was nicht passt"). Eine rote Ampel ohne
 * diese drei Antworten ist eine Meldung, mit der niemand etwas anfangen kann:
 *
 * * `detail` — **was** gemessen wurde („Letzter Lauf fehlgeschlagen vor 3 Tagen").
 * * `impact` — **warum das zählt** („Ohne geprüfte Sicherung ist die
 *   Wiederherstellung eine Vermutung"). Nur bei Problemen gefüllt.
 * * `remedy` — **was zu tun ist**, konkret genug zum Abarbeiten (Datei, Befehl,
 *   Runbook). Nur bei Problemen gefüllt.
 */
export const cockpitHealthComponentSchema = z.object({
  key: z.string(),
  label: z.string(),
  status: cockpitHealthStatusSchema,
  /** Antwortzeit in Millisekunden (falls gemessen). */
  latencyMs: z.number().nullable(),
  /** Menschlich lesbarer Zusatz (z. B. „Letztes Backup vor 3 h" oder Fehlertext). */
  detail: z.string().nullable(),
  /** Folge, wenn es so bleibt — nur bei `degraded`/`down` gefüllt. */
  impact: z.string().nullable(),
  /** Konkreter nächster Schritt — nur bei `degraded`/`down` gefüllt. */
  remedy: z.string().nullable(),
  /**
   * **Welche** — die konkret betroffenen Einträge: Konten, Jobs, Migrationen.
   * „3 von 12 Konten im Klartext" ist eine Zahl, keine Arbeitsanweisung; erst
   * die Namen machen daraus etwas Abarbeitbares (Rückfrage Tech-Lead
   * 2026-08-14: „Welche Konten sind betroffen???"). Leer, wo es nichts
   * aufzuzählen gibt.
   */
  affected: z.array(z.string()),
  /** Alter der letzten Meldung in Stunden (nur bei Heartbeat-Prüfungen). */
  ageHours: z.number().nullable(),
  /** Ab wann eine Meldung als überfällig gilt (nur bei Heartbeat-Prüfungen). */
  maxAgeHours: z.number().nullable(),
});
export type CockpitHealthComponent = z.infer<typeof cockpitHealthComponentSchema>;

export const cockpitRuntimeSchema = z.object({
  nodeVersion: z.string(),
  environment: z.string(),
  uptimeSeconds: z.number(),
  memoryRssMb: z.number(),
  memoryHeapUsedMb: z.number(),
  timestamp: z.string().datetime(),
});
export type CockpitRuntime = z.infer<typeof cockpitRuntimeSchema>;

/** Eine Kennzahl aus dem Datenbestand (z. B. „Aktive Projekte: 42"). */
export const cockpitMetricSchema = z.object({
  key: z.string(),
  label: z.string(),
  value: z.number(),
});
export type CockpitMetric = z.infer<typeof cockpitMetricSchema>;

/** Auslastung einer Hintergrund-Queue (BullMQ). */
export const cockpitQueueSchema = z.object({
  name: z.string(),
  label: z.string(),
  waiting: z.number(),
  active: z.number(),
  delayed: z.number(),
  failed: z.number(),
  completed: z.number(),
});
export type CockpitQueue = z.infer<typeof cockpitQueueSchema>;

/**
 * Die größten Tabellen der Datenbank. Beantwortet die Frage, die auf die
 * DB-Größe folgt: **wovon** besteht sie? Ein Cockpit, das nur „2,4 GB" sagt,
 * lässt einen mit der Zahl allein.
 */
export const cockpitTableSizeSchema = z.object({
  name: z.string(),
  sizeBytes: z.number(),
  sizePretty: z.string(),
  /** Geschätzte Zeilenzahl aus dem Planer-Statistik (`reltuples`) — kein COUNT(*). */
  rowEstimate: z.number(),
});
export type CockpitTableSize = z.infer<typeof cockpitTableSizeSchema>;

/**
 * Ergebnis einer Aufräum-Aktion an einer Warteschlange (fehlgeschlagene Jobs
 * erneut versuchen oder verwerfen). `affected` ist die Zahl der tatsächlich
 * angefassten Jobs — nicht die vorher angezeigte: Zwischen Anzeige und Klick
 * kann ein Lauf durchgegangen sein.
 */
export const queueMaintenanceResultSchema = z.object({
  queue: z.string(),
  label: z.string(),
  action: z.enum(['retry', 'discard']),
  affected: z.number().int(),
});
export type QueueMaintenanceResult = z.infer<typeof queueMaintenanceResultSchema>;

export const cockpitDatabaseSchema = z.object({
  sizeBytes: z.number().nullable(),
  sizePretty: z.string().nullable(),
  /** Größte Tabellen, absteigend. Leer, wenn nicht ermittelbar. */
  topTables: z.array(cockpitTableSizeSchema),
});
export type CockpitDatabase = z.infer<typeof cockpitDatabaseSchema>;

/**
 * Änderungen je Tag aus dem Audit-Log. Die einzige echte Zeitreihe, die das
 * Cockpit ohne zusätzliche Speicherung hat — und die aussagekräftigste: Ein
 * Einbruch auf null heißt, dass niemand mehr arbeitet (oder nichts mehr
 * ankommt), ein Ausschlag mitten in der Nacht ist eine Frage wert.
 */
export const cockpitActivityPointSchema = z.object({
  /** Kalendertag als ISO-Datum (JJJJ-MM-TT, Serverzeit). */
  day: z.string(),
  count: z.number(),
});
export type CockpitActivityPoint = z.infer<typeof cockpitActivityPointSchema>;

export const cockpitResponseSchema = z.object({
  /** Gesamtampel: down überstrahlt degraded überstrahlt up (not_configured zählt nicht als krank). */
  overallStatus: cockpitHealthStatusSchema,
  generatedAt: z.string().datetime(),
  health: z.array(cockpitHealthComponentSchema),
  runtime: cockpitRuntimeSchema,
  database: cockpitDatabaseSchema,
  data: z.array(cockpitMetricSchema),
  queues: z.array(cockpitQueueSchema),
  /** Änderungen je Tag (älteste zuerst), für den Aktivitätsverlauf. */
  activity: z.array(cockpitActivityPointSchema),
});
export type CockpitResponse = z.infer<typeof cockpitResponseSchema>;

/** Labels der Gesundheits-Status (UI + PDF). */
export const COCKPIT_STATUS_LABELS: Record<CockpitHealthStatus, string> = {
  up: 'In Ordnung',
  degraded: 'Beeinträchtigt',
  down: 'Ausfall',
  not_configured: 'Nicht eingerichtet',
};

/**
 * Gesamtampel aus mehreren Komponenten ableiten: irgendeine `down` → down,
 * sonst irgendeine `degraded` → degraded, sonst up. `not_configured` allein
 * gilt nicht als krank (optionale Infrastruktur).
 */
export function cockpitOverallStatus(components: Array<{ status: CockpitHealthStatus }>): CockpitHealthStatus {
  if (components.some((component) => component.status === 'down')) return 'down';
  if (components.some((component) => component.status === 'degraded')) return 'degraded';
  if (components.some((component) => component.status === 'up')) return 'up';
  return 'not_configured';
}

/**
 * Gesundheit eines Heartbeats (z. B. Backup-Job) aus Alter + gemeldetem Status
 * ableiten. Reine Funktion → testbar, von Server und ggf. UI teilbar.
 *
 * - kein Heartbeat je gemeldet → `not_configured`
 * - zuletzt Fehler gemeldet → `down`
 * - Alter ≤ maxAgeHours → `up`
 * - Alter ≤ 2× maxAgeHours → `degraded` (ein Lauf ausgefallen)
 * - älter → `down`
 */
export function heartbeatHealth(params: {
  occurredAt: string | null;
  reportedStatus: string | null;
  maxAgeHours: number;
  nowMs: number;
}): { status: CockpitHealthStatus; ageHours: number | null } {
  if (!params.occurredAt) return { status: 'not_configured', ageHours: null };
  const occurredMs = new Date(params.occurredAt).getTime();
  const ageHours = Math.max(0, (params.nowMs - occurredMs) / 3_600_000);
  if (params.reportedStatus && params.reportedStatus.toLowerCase() !== 'ok') {
    return { status: 'down', ageHours };
  }
  if (ageHours <= params.maxAgeHours) return { status: 'up', ageHours };
  if (ageHours <= params.maxAgeHours * 2) return { status: 'degraded', ageHours };
  return { status: 'down', ageHours };
}

/** Alter human-lesbar für Detailtexte (z. B. „vor 3 h", „vor 2 Tagen"). */
export function formatAgeHours(ageHours: number | null): string {
  if (ageHours == null) return 'noch nie';
  if (ageHours < 1) return 'vor wenigen Minuten';
  if (ageHours < 24) return `vor ${Math.round(ageHours)} h`;
  const days = Math.round(ageHours / 24);
  return `vor ${days} ${days === 1 ? 'Tag' : 'Tagen'}`;
}

/**
 * Sortier-Rang eines Status: **Kaputtes zuerst.** Ein Cockpit, das alphabetisch
 * oder nach Prüfreihenfolge sortiert, versteckt den Ausfall zwischen neun
 * grünen Kacheln — man findet ihn erst, wenn man alle liest.
 */
export const COCKPIT_STATUS_RANK: Record<CockpitHealthStatus, number> = {
  down: 0,
  degraded: 1,
  not_configured: 2,
  up: 3,
};

/** Bausteine nach Dringlichkeit sortieren; bei gleichem Rang bleibt die Reihenfolge. */
export function sortCockpitHealth<T extends { status: CockpitHealthStatus }>(components: readonly T[]): T[] {
  return [...components].sort((a, b) => COCKPIT_STATUS_RANK[a.status] - COCKPIT_STATUS_RANK[b.status]);
}

/**
 * Die Bausteine, die eine Handlung verlangen — `down` und `degraded`, nach
 * Dringlichkeit. `not_configured` gehört NICHT dazu: Optionale Infrastruktur,
 * die bewusst fehlt (z. B. Redis), ist kein Problem, das jemand abarbeiten muss.
 */
export function cockpitProblems<T extends { status: CockpitHealthStatus }>(components: readonly T[]): T[] {
  return sortCockpitHealth(components.filter((c) => c.status === 'down' || c.status === 'degraded'));
}

/** Wie viele Bausteine je Status — für die Übersicht am Kopf der Seite. */
export function cockpitStatusCounts(
  components: readonly { status: CockpitHealthStatus }[],
): Record<CockpitHealthStatus, number> {
  const counts: Record<CockpitHealthStatus, number> = { up: 0, degraded: 0, down: 0, not_configured: 0 };
  for (const component of components) counts[component.status] += 1;
  return counts;
}

/**
 * Ein Satz, der die Gesamtlage benennt — die Überschrift der Ampel. Bewusst
 * konkret („1 Ausfall, 2 beeinträchtigt") statt „Es liegen Probleme vor": Die
 * Zahl entscheidet, ob man das Handy weglegt oder den Laptop aufklappt.
 */
export function cockpitSummaryLine(components: readonly { status: CockpitHealthStatus }[]): string {
  const counts = cockpitStatusCounts(components);
  const parts: string[] = [];
  if (counts.down > 0) parts.push(`${counts.down} ${counts.down === 1 ? 'Ausfall' : 'Ausfälle'}`);
  if (counts.degraded > 0) parts.push(`${counts.degraded} beeinträchtigt`);
  if (parts.length === 0) {
    const checked = counts.up + counts.not_configured;
    return counts.up === 0
      ? 'Nichts eingerichtet, was geprüft werden könnte.'
      : `Alle ${checked} geprüften Bausteine in Ordnung.`;
  }
  return `${parts.join(', ')} — bitte ansehen.`;
}

/**
 * Position eines Heartbeat-Alters auf der Skala „frisch → überfällig → Ausfall",
 * als Anteil 0…1 für den Balken. Die Skala endet beim doppelten Grenzwert (dort
 * schlägt `heartbeatHealth` auf `down` um); alles darüber bleibt am Anschlag.
 */
export function heartbeatGaugeRatio(ageHours: number | null, maxAgeHours: number | null): number | null {
  if (ageHours == null || !maxAgeHours) return null;
  return Math.min(1, Math.max(0, ageHours / (maxAgeHours * 2)));
}
